/**
 * OrbitalBombardmentSkillEffect — apotheosis_meteo (Astral dense-path apotheosis).
 *
 * A mobile barrage: 1-second channel locks the player in place, then 6 seconds
 * of free movement while meteors fire continuously at the cursor position every
 * 500ms. Each meteor impact deals AoE damage and spawns a gravity distortion
 * zone identical to Meteo's (using the same resolver keys, modified by
 * meteo_upgrade_1 if unlocked).
 *
 * Phase sequence: toggle — 1000ms channel (movement locked) → 6000ms barrage
 * (free movement, meteor stream) → very_long cooldown (30000ms).
 * Second activation during the barrage triggers immediate deactivation.
 *
 * Gravity Scar interaction: kills during the barrage are sourced as
 * 'apotheosis_meteo'. The meteo_passive death trigger should register a
 * separate trigger for 'apotheosis_meteo' so gravity scars spawn on
 * Bombardment kills.
 *
 * OR-gate unlock: SkillProgressionComponent checks
 *   `parentIds.some(id => unlockedNodes.has(id))` for this node's parentIds
 *   array (`["meteo_passive", "slingshot_passive"]`). Orbital Bombardment is
 *   reachable via either the Meteo sub-path or the Slingshot sub-path.
 *
 * Attunement cost: 1 slot (all active skills, apotheosis included, cost exactly 1
 * slot — design-decisions.md §1. The JSON description says "2 slots" but that text
 * is incorrect per the V4 design doc; do not implement 2-slot cost.)
 *
 * Design values (from design-decisions.md § Astral Flags):
 * - Meteor fire rate: 2/sec (500ms interval)
 * - Impact damage per meteor: 15 (same as Meteo)
 * - Distortion zone radius: 1.5 tiles (base, modified by meteo_upgrade_1)
 * - Distortion zone duration: 2000ms (base, modified by meteo_upgrade_1)
 * - Channel duration: 1000ms
 * - Barrage duration: 6000ms
 * - Cooldown: 30000ms (very_long apotheosis tier)
 *
 * Pure logic — no Phaser dependencies.
 *
 * @see astral-spec.md § apotheosis_meteo
 * @see design-decisions.md § Astral Flags (Orbital Bombardment fire rate)
 * @see ADR-064 (attunement shared systems architecture)
 */

import { ToggleSkillEffect } from '../../ToggleSkillEffect.js';
import type { MeteoZoneConfig } from './MeteoSkillEffect.js';

// ---------------------------------------------------------------------------
// Dependency interfaces
// ---------------------------------------------------------------------------

export interface OrbitalBombardmentDeps {
  readonly resolver: {
    getAdditiveEffect(skillId: string, key: string): number;
  };
  readonly zones: {
    create(config: MeteoZoneConfig): string;
  };
  readonly targeting: {
    /** Returns the current cursor/aim position in pixel space. */
    getTargetPosition(): { x: number; y: number };
  };
  readonly movement: {
    lock(): void;
    unlock(): void;
  };
  readonly enemies: {
    getEnemiesInRadius(position: { x: number; y: number }, radiusTiles: number): string[];
  };
  readonly combat: {
    dealDamage(enemyId: string, damage: number): void;
  };
}

// ---------------------------------------------------------------------------
// Config
// ---------------------------------------------------------------------------

export interface OrbitalBombardmentConfig {
  /** Channel/windup duration in ms (1000). */
  readonly channelDurationMs: number;
  /** Duration of the free-movement barrage in ms (6000). */
  readonly barrageDurationMs: number;
  /** Very long cooldown tier in ms (30000). */
  readonly cooldownMs: number;
  /** Interval between meteor fires in ms (500 = 2/sec). */
  readonly meteorIntervalMs: number;
  /** Damage dealt per meteor impact (15 — same as Meteo). */
  readonly impactDamagePerMeteor: number;
  /** Base impact/zone radius in tiles (1.5 — same as Meteo). */
  readonly baseImpactRadiusTiles: number;
  /** Base gravity distortion zone duration in ms (2000 — same as Meteo). */
  readonly baseDistortionDurationMs: number;
  /** Speed multiplier inside distortion zones (0.5 = 50% slow). */
  readonly distortionSpeedMultiplier: number;
}

const DEFAULT_CONFIG: OrbitalBombardmentConfig = {
  channelDurationMs: 1000,
  barrageDurationMs: 6000,
  cooldownMs: 30000,       // very_long apotheosis tier
  meteorIntervalMs: 500,   // 2 meteors per second
  impactDamagePerMeteor: 15,
  baseImpactRadiusTiles: 1.5,
  baseDistortionDurationMs: 2000,
  distortionSpeedMultiplier: 0.5,
};

// ---------------------------------------------------------------------------
// Skill effect
// ---------------------------------------------------------------------------

type BombardmentPhase = 'channel' | 'barrage';

export class OrbitalBombardmentSkillEffect extends ToggleSkillEffect {
  readonly skillId = 'apotheosis_meteo';

  private readonly deps: OrbitalBombardmentDeps;
  readonly config: OrbitalBombardmentConfig;

  /** Whether the skill is in channel or barrage sub-phase. */
  private _subPhase: BombardmentPhase = 'channel';

  /** Countdown for the channel (windup) phase. */
  private _channelTimer = 0;

  /** Countdown for the barrage duration. */
  private _barrageTimer = 0;

  /** Accumulator for the meteor fire interval. */
  private _meteorAccumulator = 0;

  /** Whether player movement is currently locked. */
  private _movementLocked = false;

  /** Total activations since construction. */
  private _activationsCount = 0;

  /** Cumulative meteors fired since construction. */
  private _totalMeteorsFired = 0;

  /** Cumulative damage dealt since construction. */
  private _totalDamageDealt = 0;

  /** Cumulative zones created since construction. */
  private _totalZonesCreated = 0;

  constructor(deps: OrbitalBombardmentDeps, config: Partial<OrbitalBombardmentConfig> = {}) {
    super();
    this.deps = deps;
    this.config = { ...DEFAULT_CONFIG, ...config };
  }

  // --- State getters ---

  get subPhase(): BombardmentPhase { return this._subPhase; }
  get channelTimer(): number { return this._channelTimer; }
  get barrageTimer(): number { return this._barrageTimer; }
  get movementLocked(): boolean { return this._movementLocked; }
  get activationsCount(): number { return this._activationsCount; }
  get totalMeteorsFired(): number { return this._totalMeteorsFired; }
  get totalDamageDealt(): number { return this._totalDamageDealt; }
  get totalZonesCreated(): number { return this._totalZonesCreated; }

  // ---------------------------------------------------------------------------
  // ToggleSkillEffect implementation
  // ---------------------------------------------------------------------------

  canActivate(): boolean {
    return this.cooldownRemaining <= 0 && !this._isActive;
  }

  /**
   * Activate the bombardment, or — if already active — trigger early deactivation.
   *
   * First call: enter channel phase (1000ms movement lock).
   * Second call during barrage: immediate end, starts cooldown.
   */
  activate(): boolean {
    if (this._isActive) {
      // Manual deactivation — pressing skill again during barrage ends it early.
      this.deactivate();
      return true;
    }

    if (!this.canActivate()) return false;

    this._subPhase = 'channel';
    this._channelTimer = this.config.channelDurationMs;
    this._barrageTimer = this.config.barrageDurationMs;
    this._meteorAccumulator = 0;
    this._isActive = true;
    this._activationsCount++;

    // Lock player movement for the channel/windup phase.
    this._lockMovement();

    return true;
  }

  protected override updateWhileActive(delta: number): void {
    if (this._subPhase === 'channel') {
      this._channelTimer = Math.max(0, this._channelTimer - delta);
      if (this._channelTimer <= 0) {
        // Channel complete — transition to barrage.
        this._unlockMovement();
        this._subPhase = 'barrage';
        // Fire the first meteor immediately at barrage start.
        this._fireMeteor();
      }
      return;
    }

    // Barrage phase — fire meteors at interval, count down duration.
    this._barrageTimer = Math.max(0, this._barrageTimer - delta);
    this._meteorAccumulator += delta;

    while (this._meteorAccumulator >= this.config.meteorIntervalMs) {
      this._meteorAccumulator -= this.config.meteorIntervalMs;
      this._fireMeteor();
    }

    if (this._barrageTimer <= 0) {
      this.deactivate();
    }
  }

  protected override onDeactivate(): void {
    this._unlockMovement();
    this.cooldownRemaining = this.config.cooldownMs;
  }

  // --- Internal helpers ---

  private _lockMovement(): void {
    if (!this._movementLocked) {
      this.deps.movement.lock();
      this._movementLocked = true;
    }
  }

  private _unlockMovement(): void {
    if (this._movementLocked) {
      this.deps.movement.unlock();
      this._movementLocked = false;
    }
  }

  private _fireMeteor(): void {
    // Resolve upgrade-modified zone parameters (same keys as Meteo).
    const radiusBonus = this.deps.resolver.getAdditiveEffect('meteo', 'impactRadiusTiles');
    const resolvedRadius = this.config.baseImpactRadiusTiles + radiusBonus;

    const durationBonus = this.deps.resolver.getAdditiveEffect('meteo', 'distortionDurationMs');
    const resolvedDuration = this.config.baseDistortionDurationMs + durationBonus;

    // Target position is the cursor/aim point — meteors follow it in real time.
    const position = this.deps.targeting.getTargetPosition();

    // Deal AoE damage to enemies in the impact radius.
    const targets = this.deps.enemies.getEnemiesInRadius(position, resolvedRadius);
    for (const enemyId of targets) {
      this.deps.combat.dealDamage(enemyId, this.config.impactDamagePerMeteor);
      this._totalDamageDealt += this.config.impactDamagePerMeteor;
    }

    // Spawn gravity distortion zone at impact point (identical to Meteo's zones).
    const zoneId = `orbital_bombardment_distortion_${this._activationsCount - 1}_${this._totalMeteorsFired}`;
    this.deps.zones.create({
      id: zoneId,
      type: 'gravity_distortion',
      sourceSkillId: 'apotheosis_meteo',
      shape: 'circle',
      position: { ...position },
      radiusTiles: resolvedRadius,
      durationMs: resolvedDuration,
      speedMultiplier: this.config.distortionSpeedMultiplier,
    });
    this._totalZonesCreated++;
    this._totalMeteorsFired++;
  }

  override destroy(): void {
    this._unlockMovement();
    super.destroy();
  }
}
